import React from 'react'
import {
  AppBar,
  Button,
  createStyles,
  Divider,
  Hidden,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Theme,
  Toolbar,
} from '@material-ui/core'
import { MoreVertOutlined } from '@material-ui/icons'
import { AppLogo } from 'components/Layout/AppLogo'
import { Container } from 'components/Layout/Container'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { SearchBomItems } from 'components/Search/SearchBomItems'
import { useAppRunningContext } from 'features/EmbeddedRh24/useAppRunningContext'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from 'store/configureStore'
import { RootAppState } from 'store/configureStore'
import UserButton from '../../UserButton/UserButton'
import {
  NewProjectButton,
  useNavbarButtonClasses,
} from './NewProjectButton/NewProjectButton'

import { useOrganizationContext } from 'utils/useOrganizationContext'
import { useAuth0 } from '@auth0/auth0-react'

import './NavBar.sass'
import { SocialMedia } from 'components/SocialMedia'
import { BeamerNotificationButton } from './BeamerNotificationButton'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: {
      height: theme.spacing(2),
      // padding: theme.spacing(1, 0),
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1em',
      maxWidth: theme.breakpoints.values.xl,
      margin: '0 auto',
      width: '100%',
    },
    logoContainer: {
      flex: '0 0 auto',
      textAlign: 'center',
      height: '100%',
      '& button': {
        height: '100%',
        padding: 0,
      },
      '& button:hover': {
        backgroundColor: 'transparent',
      },
    },
    logoImg: {
      height: `100%`,
      maxHeight: '64px',
      maxWidth: theme.spacing(20),
      mixBlendMode: theme.palette.type === 'dark' ? 'hard-light' : 'inherit',
    },
    search: {
      ['& .MuiInputBase-root']: {
        color: 'inherit',
        '& button': {
          color: 'inherit',
        },
      },
      flex: '1 0 auto',
    },
    fieldSection: {
      display: 'flex',
      height: '100%',
      color: `${theme.palette.getContrastText(
        theme.overrides?.MuiAppBar?.colorPrimary['backgroundColor'] ||
          theme.palette.primary.main
      )} !important`,
    },
    sectionDesktop: {
      // display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '1em',
        flex: '1 1 auto',
      },
    },
    sectionMobile: {
      display: 'flex',
      marginLeft: 'auto',
      [theme.breakpoints.up('md')]: {
        // display: 'none'
      },
    },
  })
)

const MyProjectsButton = () => {
  const classes = useNavbarButtonClasses()
  const navigate = useNavigate()

  return (
    <Button
      className={classes.button}
      data-testid="navbar-btn-my-projects"
      onAuxClick={() => window.open('/app', '_blank')}
      onClick={(e) => {
        e.preventDefault()
        navigate('/app')
      }}
      style={{ flex: '0 0 auto' }}
      disableElevation
      disableFocusRipple
      disableRipple
    >
      <LocalizedTypography
        color="inherit"
        translationKey="common:my-projects"
        translationDefaultValue={'my projects'}
      />
    </Button>
  )
}

const MobileMenu = ({ portalView }: { portalView?: boolean }) => {
  const [open, setOpen] = React.useState(false)
  const buttonRef = React.useRef(undefined)
  const { t } = useTranslation()
  return (
    <>
      <IconButton
        color="inherit"
        ref={buttonRef}
        onClick={() => setOpen((state) => !state)}
      >
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
      >
        {!portalView ? (
          <>
            <MenuItem style={{ display: 'flex', justifyContent: 'center' }}>
              <SocialMedia />
            </MenuItem>
            <Divider />
          </>
        ) : null}
        <MenuItem>
          <MyProjectsButton />
        </MenuItem>
        <MenuItem>
          <NewProjectButton />
        </MenuItem>
        <MenuItem>
          <UserButton />
        </MenuItem>
        {!portalView ? (
          <div>
            <Divider />
            <MenuItem id="app-notification-button">
              {t('common:qf-road-map-notifications', 'roadmap / notifications')}
            </MenuItem>
          </div>
        ) : null}
      </Menu>
    </>
  )
}

const NavBar = () => {
  const navigate = useNavigate()
  const { organizationId } = useOrganizationContext()

  const showBeamerIntegration = useAppSelector(
    (state) => state.user?.organizationContext?.showBeamerNotifications
  )

  const { isAuthenticated } = useAuth0()
  const userProfile = useAppSelector(
    (state: RootAppState) => state.user.profile
  )

  const classes = useStyles()

  const hasActiveProject = useAppSelector((state: RootAppState) =>
    Boolean(state.project.activeProject)
  )

  const { withinIframe } = useAppRunningContext()

  const hasOrganizationContext = organizationId

  if (!isAuthenticated || !userProfile) return null

  return (
    <AppBar position={'sticky'} color="primary" style={{ height: '64px' }}>
      <div id="navbar-placeholder" className="navbar-placeholder"></div>
      <Container maxWidth="xl" style={{ width: '100%' }}>
        <Toolbar className={classes.toolbar} disableGutters>
          <div className={classes.logoContainer}>
            <Button
              onClick={() => navigate('/app')}
              onAuxClick={() => window.open('/app', '_blank')}
              disableRipple
              disableFocusRipple
              disableElevation
            >
              <AppLogo className={classes.logoImg} />
            </Button>
          </div>
          <div className={classes.search}>
            {isAuthenticated && (
              <>
                {/* <div id="navbar-placeholder" style={{ minWidth: 0, flexBasis: hasActiveProject ? '100%' : 'auto' }} /> */}
                {
                  // bomitems search bar should not be show in project page
                  !hasActiveProject && hasOrganizationContext && (
                    <SearchBomItems />
                  )
                }
              </>
            )}
          </div>
          {!withinIframe ? (
            <Hidden smDown>
              <div className={classes.fieldSection}>
                <SocialMedia />
              </div>
            </Hidden>
          ) : null}
          <div className={classes.fieldSection}>
            <Hidden smDown>
              <div className={classes.sectionDesktop}>
                {hasOrganizationContext && (
                  <>
                    <MyProjectsButton />
                    <NewProjectButton />
                  </>
                )}
                <Hidden mdDown>
                  <UserButton />
                </Hidden>
                <Hidden lgUp>
                  <MobileMenu portalView={withinIframe} />
                </Hidden>
              </div>
            </Hidden>
            <Hidden mdUp>
              <div className={classes.sectionMobile}>
                <MobileMenu portalView={withinIframe} />
              </div>
            </Hidden>
          </div>
        </Toolbar>
      </Container>
      {organizationId && (!withinIframe || showBeamerIntegration) ? (
        <Hidden mdDown>
          <BeamerNotificationButton
            style={{
              position: 'absolute',
              right: '8px',
              bottom: '1rem',
              height: '32px',
              width: '32px',
              padding: '4px',
            }}
          />
        </Hidden>
      ) : null}
    </AppBar>
  )
}

export default NavBar
